import { FC, useEffect, useState } from "react";

import {
  Alert,
  Button,
  Checkbox,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import {
  GeoDataFileProcessingRunModel,
  listGeoDataFileProcessingRun,
  listSubregion,
  postRegion,
  SubregionModel,
} from "@rubi/api-generated";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Crumb, DevPage, Section } from "./common";
import { RegionBreadcrumbs } from "./regionShared";

export const DevRegionCreatePage: FC = () => {
  const { orgId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [runs, setRuns] = useState<GeoDataFileProcessingRunModel[]>([]);
  const [runId, setRunId] = useState(searchParams.get("runId") ?? "");
  const [subregions, setSubregions] = useState<SubregionModel[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listGeoDataFileProcessingRun({ path: { org_id: orgId } }).then((response) =>
      setRuns(
        (response.data?.geo_data_file_processing_runs ?? []).filter(
          (run) => run.status === "processed",
        ),
      ),
    );
  }, [orgId]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSubregions([]);
    if (!runId) return;

    let cancelled = false;
    listSubregion({
      path: { org_id: orgId },
      query: { geo_data_file_processing_run_id: runId },
    }).then((response) => {
      if (cancelled) return;
      setSubregions(response.data?.subregions ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId, runId]);

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(
      checked ? new Set(subregions.map((sr) => sr.id)) : new Set(),
    );
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await postRegion({
        path: { org_id: orgId },
        body: {
          geo_data_file_processing_run_id: runId,
          subregion_ids: [...selectedIds],
        },
      });
      const region = response.data;
      if (!region) throw new Error("No region returned");
      navigate(`/dev_admin/region/${orgId}/region/${region.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create region");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DevPage
      title="Create Region"
      breadcrumbs={
        <RegionBreadcrumbs orgId={orgId}>
          <Crumb>Create</Crumb>
        </RegionBreadcrumbs>
      }
    >
      <Section subtitle="1. Pick a processing run">
        <TextField
          select
          size="small"
          label="Processing run"
          value={runId}
          onChange={(e) => setRunId(e.target.value)}
          sx={{ minWidth: 320 }}
          helperText="A Region can only be composed from Subregions extracted by one run."
        >
          {runs.map((run) => (
            <MenuItem key={run.id} value={run.id}>
              {run.id}
            </MenuItem>
          ))}
        </TextField>
      </Section>

      {runId && (
        <Section subtitle="2. Pick its Subregions">
          <table className="dev-table">
            <thead>
              <tr>
                <th style={{ width: 1 }}>
                  <Checkbox
                    checked={
                      subregions.length > 0 &&
                      selectedIds.size === subregions.length
                    }
                    indeterminate={
                      selectedIds.size > 0 &&
                      selectedIds.size < subregions.length
                    }
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th>id</th>
                <th>metadata</th>
              </tr>
            </thead>
            <tbody>
              {subregions.map((sr) => (
                <tr key={sr.id}>
                  <td>
                    <Checkbox
                      checked={selectedIds.has(sr.id)}
                      onChange={() => toggleOne(sr.id)}
                    />
                  </td>
                  <td className="mono">{sr.id}</td>
                  <td>{JSON.stringify(sr.metadata)}</td>
                </tr>
              ))}
              {subregions.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted">
                    No Subregions from this run.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={selectedIds.size === 0 || submitting}
              onClick={handleCreate}
            >
              {submitting
                ? "Creating…"
                : `Create Region (${selectedIds.size} subregion${selectedIds.size === 1 ? "" : "s"})`}
            </Button>
          </Stack>
        </Section>
      )}
    </DevPage>
  );
};
